import {reactive} from "vue";
import {
    BUSINESS_DOCUMENT_TYPE,
    BUSINESS_STATUS_ENUM,
    BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE
} from "@/models/businessStatusEnum";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";

// Émis par les tableaux de contrats après un rechargement de leurs données (hors premier chargement), pour mettre à jour
// les compteurs du menu (BusinessesHeader)
export const COUNTS_REFRESH_EVENT = "business-counts-refresh";

// Compteurs du menu Prestations, partagés entre les pages : le menu est recréé à chaque changement de rubrique et réaffiche
// ces valeurs tout de suite, sans nouvelle requête tant qu'elles sont récentes. `token` : ne pas réutiliser ceux d'un autre utilisateur.
export const businessCountsCache = reactive({counts: undefined, loadedAt: 0, token: undefined});
export const BUSINESS_COUNTS_MAX_AGE = 2 * 60 * 1000;

// Un devis est "non envoyé" (en rédaction) tant qu'il n'a pas été envoyé, "envoyé" (en cours) à partir de l'envoi inclus (envoyé,
// accepté, en cours de réalisation, réalisation terminée). Le point de bascule (SENT) et les statuts de chaque côté sont
// déduits de l'enchaînement normal d'un devis. Les devis refusés ou annulés ne sont que dans l'onglet "Tous" et sans sous-filtre.
const quotePipeline = BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE[BUSINESS_DOCUMENT_TYPE.QUOTE];
const sentIndex = quotePipeline.indexOf(BUSINESS_STATUS_ENUM.SENT);

// Filtre secondaire « non envoyés / envoyés » : le premier est sélectionné par défaut, un clic sur le filtre actif le désélectionne
// (aucun sous-filtre = tous les devis de l'onglet).
const statusSubFilter = (label, statuses) => ({
    label,
    filter: {field: "status", operator: FILTER_OPERATOR_ENUM.IN.value, variable: statuses.map(status => status.value)}
});
const quoteSubFilters = [
    statusSubFilter("Non envoyés", quotePipeline.slice(0, sentIndex)),
    statusSubFilter("Envoyés", quotePipeline.slice(sentIndex)),
];

// `separatorAfter` : trait de séparation sous l'onglet dans le menu ("Tous" résume les autres rubriques).
// Onglets de la page Business. Chaque onglet affiche un BusinessTable :
//  - `filters`    : filtres par défaut (remplacent les filtres par défaut de BusinessTable sur le même champ) ;
//  - `subFilters` : filtre secondaire affiché au-dessus du tableau, qui s'ajoute à `filters` ;
//  - `notice`     : encart bleu affiché au-dessus du tableau pour expliquer que la liste est filtrée ;
//  - `document`   : document sur lequel s'ouvre la modale d'un contrat (devis par défaut) ;
//  - `table`      : tableau propre à l'onglet quand ce n'est pas un BusinessTable ("deposits", "creditNotes").
// Un onglet sans `filters` (ex. "unpaid") a sa propre vue/route.
// Ajouter un onglet = ajouter une entrée ici (le path doit être /businesses/<key>).
export const BUSINESS_TABS = [
    {key: "all", label: "Tous", path: "/businesses", separatorAfter: true},
    {
        key: "quotes", label: "Devis", path: "/businesses/quotes",
        filters: [{field: "invoiceNumber", operator: FILTER_OPERATOR_ENUM.IN.value, variable: [undefined]}],
        subFilters: quoteSubFilters,
        notice: "Les devis affichés ici sont filtrés : seuls ceux pour lesquels vous avez des actions à faire, ou des actions en attente, sont visibles sur cette page. Pour consulter tous les devis, y compris ceux qui sont refusés ou annulés, rendez-vous dans la rubrique « Tous »."
    },
    {key: "deposits", label: "Acomptes", path: "/businesses/deposits", document: BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value, table: "deposits",
        notice: "Les acomptes affichés ici sont filtrés : seuls ceux pour lesquels vous avez des actions à faire, ou des actions en attente, sont visibles sur cette page. Pour consulter tous les acomptes, y compris ceux qui sont payés ou annulés, rendez-vous dans la rubrique « Tous »."},
    // Factures à encaisser (vue propre, route /businesses/unpaid) : ses pastilles distinguent les impayés échus de ceux qui arrivent à échéance
    {key: "unpaid", label: "Factures", path: "/businesses/unpaid",
        notice: "Les factures affichées ici sont filtrées : seules celles pour lesquelles vous avez des actions à faire, ou des actions en attente (encaissements), sont visibles sur cette page. Pour consulter toutes les factures, y compris celles qui sont payées ou annulées, rendez-vous dans la rubrique « Tous »."},
    {key: "credit-notes", label: "Avoirs", path: "/businesses/credit-notes", document: BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value, table: "creditNotes"},
];